import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdMenuBook, MdExpandMore, MdExpandLess, MdQuiz } from 'react-icons/md';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import Tabs from 'components/Tabs';
import Loader from 'components/Loader';

import './index.css';

const VISIBLE_ROWS = 3;
const COLS_PER_ROW = 4;
const INITIAL_VISIBLE = VISIBLE_ROWS * COLS_PER_ROW;

const VOCAB_TABS = ['Flashcards', 'Quiz History'];

function DeckSection({ title, decks, onDeckClick }: {
    title: string,
    decks: any[],
    onDeckClick: (vocabSetId: number) => void,
}) {
    const [expanded, setExpanded] = useState(false);
    const hasMore = decks.length > INITIAL_VISIBLE;
    const visible = expanded ? decks : decks.slice(0, INITIAL_VISIBLE);

    return (
        <div className='vl__section'>
            <h2 className='vl__sectionTitle'>{title}</h2>
            <div className='vl__grid'>
                {visible.map(assignment => {
                    const masteryPct = assignment.wordCount > 0
                        ? Math.round((assignment.mastered / assignment.wordCount) * 100)
                        : 0;

                    return (
                        <button
                            className='vl__deck'
                            onClick={() => onDeckClick(assignment.vocabSetId)}
                            key={assignment.id}
                        >
                            <div className='vl__deckStack'>
                                <div className='vl__deckCard vl__deckCard--3'></div>
                                <div className='vl__deckCard vl__deckCard--2'></div>
                                <div className='vl__deckCard vl__deckCard--1'>
                                    <MdMenuBook className='vl__deckIcon' />
                                    <span className='vl__deckCount'>{assignment.wordCount} cards</span>
                                </div>
                            </div>

                            <h3 className='vl__deckName'>{assignment.subject}</h3>

                            <div className='vl__progressBar'>
                                <span className='vl__progressFill' style={{ width: `${masteryPct}%` }}></span>
                            </div>

                            {assignment.status === 'not_started' ? (
                                <p className='vl__deckMeta'>
                                    Assigned {new Date(assignment.createdAt).toLocaleDateString()}
                                </p>
                            ) : assignment.status === 'completed' ? (
                                <p className='vl__deckMeta vl__deckMeta--completed'>
                                    Completed · {assignment.mastered}/{assignment.wordCount} mastered
                                </p>
                            ) : (
                                <p className='vl__deckMeta'>
                                    {assignment.mastered} mastered · {assignment.learning} learning
                                </p>
                            )}
                        </button>
                    );
                })}
            </div>
            {hasMore && (
                <button className='vl__showMore' onClick={() => setExpanded(!expanded)}>
                    {expanded ? (
                        <><MdExpandLess /> Show less</>
                    ) : (
                        <><MdExpandMore /> Show {decks.length - INITIAL_VISIBLE} more</>
                    )}
                </button>
            )}
        </div>
    );
}

function Vocab() {
    const [assignments, setAssignments] = useState(null);
    const [quizSets, setQuizSets] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const initialTab = (location.state as any)?.tab || 0;
    const [tabIndex, setTabIndex] = useState(initialTab);
    const { organization } = useAppSelector(state => state.organization);

    const fetchData = () => {
        axios.get(`vocab/assignments/${organization.urlSlug}/`)
            .then(res => setAssignments(res.data.assignments))
            .catch(error => {
                // Only a 401 (deactivated membership) replaces the page with
                // the Unauthorized screen; a transient failure on the window
                // -focus refetch must not blank an already-rendered page.
                if (error.response?.status === 401) dispatch(setError(error));
                else console.error(error);
            });

        axios.get(`vocab/quiz/sets/${organization.urlSlug}/`)
            .then(res => {
                const map = {};
                for (const s of res.data.sets) {
                    map[s.studentVocabSetId] = s;
                }
                setQuizSets(map);
            })
            .catch(() => setQuizSets(null));
    };

    useEffect(() => {
        fetchData();

        // Re-fetch when user navigates back to this page
        const handleFocus = () => fetchData();
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    if (assignments === null) return <Loader />;

    const inProgress = assignments.filter(a => a.status === 'in_progress');
    const notStarted = assignments.filter(a => a.status === 'not_started');
    const completed = assignments.filter(a => a.status === 'completed');

    const handleDeckClick = (vocabSetId: number) => navigate(`/vocab/flashcards/${vocabSetId}`);

    // Build flat history list
    const allAttempts = quizSets
        ? Object.entries(quizSets).flatMap(([svsId, setData]: [string, any]) => {
            const assignment = assignments.find(a => a.id === parseInt(svsId));
            const completedAttempts = setData.completedAttempts || [];
            const testNumberCounts: Record<number, number> = {};
            return completedAttempts.map(attempt => {
                const tn = attempt.testNumber;
                testNumberCounts[tn] = (testNumberCounts[tn] || 0) + 1;
                const n = testNumberCounts[tn];
                return {
                    ...attempt,
                    subject: setData.subject || assignment?.subject || 'Unknown',
                    quizLabel: n > 1 ? `Quiz ${tn} Attempt ${n}` : `Quiz ${tn}`,
                };
            });
        }).sort((a, b) => new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime())
        : [];

    if (assignments.length === 0) {
        return (
            <div className='layout'>
                <Helmet><title>Vocab - {organization.name}</title></Helmet>
                <div className='vl'>
                    <h1 className='vl__title'>Vocab</h1>
                    <div className='vl__empty'>
                        <MdMenuBook className='vl__emptyIcon' />
                        <p className='vl__emptyText'>No vocab assignments yet.</p>
                    </div>
                </div>
            </div>
        );
    }

    const hasQuizAccess = quizSets !== null;

    return (
        <div className='layout'>
            <Helmet><title>Vocab - {organization.name}</title></Helmet>
            <div className='vl'>
                <h1 className='vl__title'>Vocab</h1>

                {hasQuizAccess && (
                    <Tabs
                        className='vl__tabs'
                        tabs={VOCAB_TABS}
                        tabIndex={tabIndex}
                        onSelect={VOCAB_TABS.map((_tab, t) => () => setTabIndex(t))}
                    />
                )}

                {/* Flashcards tab */}
                {tabIndex === 0 && (
                    <>
                        {inProgress.length > 0 && <DeckSection title='In Progress' decks={inProgress} onDeckClick={handleDeckClick} />}
                        {notStarted.length > 0 && <DeckSection title='Assigned' decks={notStarted} onDeckClick={handleDeckClick} />}
                        {completed.length > 0 && <DeckSection title='Completed' decks={completed} onDeckClick={handleDeckClick} />}
                    </>
                )}

                {/* Quiz History tab */}
                {tabIndex === 1 && (
                    <div className='vl__history'>
                        {allAttempts.length === 0 ? (
                            <div className='vl__empty'>
                                <MdQuiz className='vl__emptyIcon' />
                                <p className='vl__emptyText'>No quiz attempts yet.</p>
                            </div>
                        ) : (
                            <div className='vl__historyList'>
                                {allAttempts.map(attempt => (
                                    <button
                                        key={attempt.id}
                                        className='vl__historyItem'
                                        onClick={() => navigate(`/vocab/quiz/result/${attempt.id}`, { state: { from: 'history' } })}
                                    >
                                        <div className='vl__historyLeft'>
                                            <p className='vl__historySubject'>{attempt.subject}</p>
                                            <p className='vl__historyMeta'>
                                                {attempt.quizLabel} · {new Date(attempt.completedDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className='vl__historyRight'>
                                            <span className={`vl__historyScore ${attempt.score >= 80 ? 'vl__historyScore--high' : attempt.score >= 60 ? 'vl__historyScore--mid' : 'vl__historyScore--low'}`}>
                                                {attempt.score}%
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Vocab;
